import { Component, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';

@Component({
  selector: 'app-ai-powered-recruitment-tool',
  imports: [CommonModule],
  standalone: true,
  templateUrl: './ai-powered-recruitment-tool.component.html',
  styleUrl: './ai-powered-recruitment-tool.component.scss'
})
export class AiPoweredRecruitmentToolComponent implements OnInit, OnDestroy {
  private jsonLdScript: any;
  constructor(private router: Router, private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }

  nav(url: any) {
    console.log(url);
    this.router.navigateByUrl(url)
  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route: { data: any; }) => route.data)
    )
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
  injectSchemaMarkup(): void {
    const recruitmentSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/ai-powered-recruitment-tool",
          "url": "https://www.kalpitatechnologies.com/kalpita-case-studies/ai-powered-recruitment-tool",
          "name": "Solving Legacy System Challenges with Kalpita Recruitment Tool – Case Study",
          "description": "Case study on how Kalpita Technologies replaced manual Excel-based recruitment tracking with an automated, role-based recruitment platform featuring RAG chatbot integration.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en",
          "datePublished": "2025-01-28"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/ai-powered-recruitment-tool#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/", "name": "Home" } },
            { "@type": "ListItem", "position": 2, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/case-study", "name": "Case Studies" } },
            { "@type": "ListItem", "position": 3, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/ai-powered-recruitment-tool", "name": "Kalpita Recruitment Tool" } }
          ]
        },
        {
          "@type": "Article",
          "headline": "Solving Legacy System Challenges with Kalpita Recruitment Tool",
          "articleBody": "Kalpita Technologies' internal recruitment process was heavily dependent on Excel sheets, leading to data loss, inefficiencies, and a lack of structured tracking...",
          "author": { "@type": "Organization", "name": "Kalpita Technologies" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "datePublished": "2025-01-28",
          "image": "https://www.kalpitatechnologies.com/assets/resources/casestudies/ai-powered-recruitment-tool/requisitionInfo.webp"
        },
        {
          "@type": "HowTo",
          "name": "How to Build an AI-Powered Recruitment Platform",
          "step": [
            { "@type": "HowToStep", "name": "Assess Legacy Pain Points", "text": "Identify inefficiencies in existing hiring processes including Excel-based tracking." },
            { "@type": "HowToStep", "name": "Design Role-Based Management", "text": "Build a structured requisition system with multi-level approval workflows." },
            { "@type": "HowToStep", "name": "Integrate RAG Chatbot", "text": "Deploy a Retrieval-Augmented Generation chatbot connected to internal databases." }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/ai-powered-recruitment-tool#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What challenges did the legacy recruitment system have?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The internal recruitment process was heavily dependent on Excel sheets, leading to data loss, inefficiencies, and lack of structured tracking."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(recruitmentSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
}
